import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import { FontFamilies, Typography } from "@/constants/Typography";
import { Ionicons } from "@expo/vector-icons";
import React, { useState } from "react";
import {
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ScrollView,
} from "react-native";
import Icons from "@/assets/icons";
import RefundProcessServices from "@/services/refundProcessServices";
import { useQuery } from "@tanstack/react-query";
import { showError, showSuccess } from "@/utils/toast";
import { useGmailLinkWebReturn } from "@/hooks/useGmailLinkWebReturn";
import AuthServices from "@/services/authServices";
import { router, useLocalSearchParams } from "expo-router";

type Props = {
  emailProvider: string;
  emailAddress: string;
  onMenuPress?: (_provider: string) => void;
};

const EmailAccountItem: React.FC<Props> = ({
  emailProvider,
  emailAddress,
  onMenuPress,
}) => {
  return (
    <View style={styles.emailItem}>
      <View style={styles.emailLeft}>
        <Icons.Mail />
        <View style={styles.emailInfo}>
          <Text style={styles.emailProvider}>
            {emailProvider.charAt(0).toUpperCase() + emailProvider.slice(1)}
          </Text>
          <Text style={styles.emailAddress}>{emailAddress}</Text>
        </View>
      </View>

      <TouchableOpacity
        onPress={() => onMenuPress?.(emailProvider)}
        style={styles.menuButton}
      >
        <Ionicons name="trash" size={20} color="#9CA3AF" />
      </TouchableOpacity>
    </View>
  );
};

interface EmptyStateProps {
  onEmailLinked?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onEmailLinked }) => {
  const [isLinking, setIsLinking] = useState(false);
  const [isGmailLinked, setIsGmailLinked] = useState(false);

  const handleLinkGmail = async () => {
    if (isLinking) return;

    setIsLinking(true);
    try {
      const res = await AuthServices.handleGoogleAuth();

      if ("handoff" in res && res.handoff === "redirect") {
        return;
      }

      const success = res.success;

      setIsGmailLinked(success);
      if (success) {
        showSuccess("Gmail linked successfully!");
        onEmailLinked?.();
      } else {
        showError("Failed to link Gmail");
      }
    } catch (e) {
      showError("Failed to link Gmail");
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <View style={styles.emptyContainer}>
      <Icons.NoEmail width={40} height={40} />
      <Text style={styles.emptyTitle}>No linked emails</Text>
      <Text style={styles.emptySubtitle}>
        Connect your email accounts to find refunds and claims
      </Text>
      <TouchableOpacity style={styles.linkButton} onPress={handleLinkGmail}>
        <View style={styles.linkButtonContent}>
          <Icons.Gmail />
          <Text style={styles.linkButtonText}>
            {isLinking ? "Linking..." : "Gmail"}
          </Text>
        </View>
        <Icons.ChevronRight />
      </TouchableOpacity>
    </View>
  );
};

const AddEmailButton = ({ onPress }: { onPress: () => void }) => (
  <TouchableOpacity style={styles.addEmailButton} onPress={onPress}>
    <View style={styles.addEmailIconContainer}>
      <Ionicons name="add" size={24} color="#0F9129" />
    </View>
    <Text style={styles.addEmailText}>Add another email</Text>
  </TouchableOpacity>
);

const LinkedEmailsScreen = () => {
  const { returnTo, chatSessionId } = useLocalSearchParams<{
    returnTo?: string;
    chatSessionId?: string;
  }>();

  const {
    data: linkedEmails,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["linkedEmails"],
    queryFn: RefundProcessServices.getLinkedEmail,
  });

  useGmailLinkWebReturn({
    onSuccess: () => {
      refetch();
      if (returnTo === "chat") {
        router.replace({
          pathname: "/chat",
          params: chatSessionId ? { chatSessionId } : undefined,
        });
      }
    },
  });

  const handleMenuPress = (provider: string) => {
    RefundProcessServices.unlinkEmail(provider).then((result) => {
      if (result === 200) {
        showSuccess("Email unlinked successfully");
        refetch();
      } else {
        showError("Failed to unlink email");
      }
    });
  };

  const handleEmailLinked = () => {
    // Refresh the linked emails list when a new email is connected
    refetch();

    // If came from chat, navigate back to chat screen with session ID
    if (returnTo === "chat") {
      router.replace({
        pathname: "/chat",
        params: chatSessionId ? { chatSessionId } : undefined,
      });
    } else {
      router.back();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Linked email accounts" fallbackBackPath="/account" />

      <ScrollView style={styles.content}>
        {isLoading ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>Loading...</Text>
          </View>
        ) : !linkedEmails || linkedEmails.length === 0 ? (
          <EmptyState onEmailLinked={handleEmailLinked} />
        ) : (
          <>
            {linkedEmails.map((account) => (
              <EmailAccountItem
                key={account.email}
                emailProvider={account.provider}
                emailAddress={account.email}
                onMenuPress={handleMenuPress}
              />
            ))}
            <AddEmailButton
              onPress={async () => {
                const res = await AuthServices.handleGoogleAuth();
                if ("handoff" in res && res.handoff === "redirect") {
                  return;
                }
                if (res.success) {
                  handleEmailLinked();
                }
              }}
            />
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

export default LinkedEmailsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  emailItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  emailLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  emailInfo: {
    flex: 1,
    marginLeft: 12,
  },
  emailProvider: {
    ...Typography.label.large,
    color: "#1F2937",
    marginBottom: 2,
  },
  emailAddress: {
    ...Typography.caption.large,
    color: "#6B7280",
  },
  menuButton: {
    padding: 8,
  },
  emptyContainer: {
    flex: 1,
    gap: 16,
  },
  emptyTitle: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 24,
    color: "#161D14",
  },
  emptySubtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  linkButton: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#F3F7F3",
    borderRadius: 12,
    padding: 15,
    borderWidth: 1,
    borderColor: "#E1EFE4",
  },
  linkButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  linkButtonText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 17,
    color: "#161D14",
  },
  connectedRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    gap: 8,
  },
  connectedText: {
    ...Typography.body.medium,
    color: "#22C55E",
    fontWeight: "500",
  },
  addEmailButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    marginTop: 16,
  },
  addEmailIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#E8F5E8",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  addEmailText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
    color: "#0F9129",
  },
});
